import { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ReactComponent as CalendarIcon } from "../../assets/icons/calendarIcon.svg";
import { ReactComponent as TwoCalendarIcon } from "../../assets/icons/twoCalendarIcon.svg";

const formatDate = (date: Date | undefined) => {
    return date instanceof Date && !isNaN(date.getTime()) ? format(date, "yyyy. MM. dd") : "";
};

const parseDate = (input: string): Date | undefined => {
    const cleanedInput = input.replace(/\D/g, "");
    if (cleanedInput.length === 8) {
        const year = parseInt(cleanedInput.substring(0, 4), 10);
        const month = parseInt(cleanedInput.substring(4, 6), 10) - 1;
        const day = parseInt(cleanedInput.substring(6, 8), 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? undefined : date;
    }
    return undefined;
};

const formatInput = (input: string): string => {
    const cleanedInput = input.replace(/\D/g, "");
    let formatted = cleanedInput;

    if (cleanedInput.length > 4) {
        formatted = `${cleanedInput.substring(0, 4)}. ${cleanedInput.substring(4)}`;
    }
    if (cleanedInput.length > 6) {
        formatted = `${cleanedInput.substring(0, 4)}. ${cleanedInput.substring(4, 6)}. ${cleanedInput.substring(6)}`;
    }

    return formatted;
};

interface DayPickerInputProps {
    className?: string;
    initialDate?: Date;
    twoCalendar?: boolean;
    onDateChange?: (date: Date | undefined) => void;
    right?: boolean;
    disabled?: boolean;
    minDate?: Date;
    maxDate?: Date;
    /** 날짜 아래에 작은 점을 찍을 날짜들 (예: 메모가 있는 날) */
    markedDates?: Date[];
    /** 달력에 보이는 달이 바뀔 때. 표시할 달의 데이터를 미리 받아올 때 쓴다 */
    onMonthChange?: (month: Date) => void;
}
const DayPickerInput = ({
    className,
    initialDate,
    twoCalendar,
    onDateChange,
    right,
    disabled,
    maxDate,
    minDate,
    markedDates,
    onMonthChange,
}: DayPickerInputProps) => {
    const previousDateRef = useRef<Date | undefined>(undefined);

    const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
    const [inputValue, setInputValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);
    const normalizeDate = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const handleDateChange = (date: Date | undefined) => {
        if (date) {
            const selected = normalizeDate(date);
            if (minDate && selected < normalizeDate(minDate)) return;
            if (maxDate && selected > normalizeDate(maxDate)) return;
        }
        setSelectedDate(date);
        setInputValue(formatDate(date));
        onDateChange?.(date);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { selectionStart } = e.target;
        const rawValue = e.target.value;
        const formattedValue = formatInput(rawValue);

        setInputValue(formattedValue);
        const parsedDate = parseDate(rawValue);
        if (parsedDate) {
            setSelectedDate(parsedDate);
            onDateChange?.(parsedDate);
        }

        requestAnimationFrame(() => {
            if (inputRef.current && selectionStart !== null) {
                let cursorPosition = selectionStart;
                const diff = formattedValue.length - rawValue.length;
                cursorPosition += diff > 0 ? diff : 0;
                inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            }
        });
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (!initialDate) {
            setSelectedDate(undefined);
            setInputValue("");
            previousDateRef.current = undefined;
        } else {
            setSelectedDate(initialDate);
            setInputValue(formatDate(initialDate));
            previousDateRef.current = initialDate;
        }
    }, [initialDate]);

    return (
        <div className={`relative w-full flex items-center ${className}`} ref={calendarRef}>
            <input
                disabled={disabled}
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={twoCalendar ? "기간" : "날짜 선택"}
                maxLength={13}
                className="disabled:bg-white h-full z-10 focus:outline-none rounded w-full text-sm placeholder:text-grayicon"
            />
            <button disabled={disabled} type="button" className="mt-[2px]" onClick={() => setIsOpen(!isOpen)}>
                {twoCalendar ? <TwoCalendarIcon /> : <CalendarIcon />}
            </button>

            {isOpen && (
                <div
                    /* 툴바(z-10)·알림 패널(z-50) 위로 떠야 해서 z-[100]. 모달(z-[200])보다는 아래다 */
                    className={`absolute z-[100] bg-white shadow-lg py-3 pr-3 pl-4 border rounded ${right ? "left-0" : "right-0"} top-9`}
                >
                    <DayPicker
                        mode="single"
                        selected={selectedDate}
                        onSelect={date => handleDateChange(date as Date | undefined)}
                        locale={ko}
                        defaultMonth={selectedDate}
                        onMonthChange={onMonthChange}
                        modifiers={markedDates && markedDates.length > 0 ? { marked: markedDates } : undefined}
                        modifiersClassNames={{
                            /*
                            // 점을 칸 맨 위에 두면 선택 표시 테두리에 걸쳐 오류처럼 보인다.
                            // 칸 중앙(=숫자 위치) 기준으로 조금만 올려 원 안쪽, 숫자 바로 위에 붙인다.
                            */
                            marked:
                                "relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 " +
                                "after:-translate-x-1/2 after:-translate-y-[13px] " +
                                "after:w-[4px] after:h-[4px] after:rounded-full after:bg-primary1",
                        }}
                        disabled={
                            [minDate ? { before: minDate } : undefined, maxDate ? { after: maxDate } : undefined].filter(Boolean) as any
                        }
                        formatters={{
                            formatCaption: month => format(month, "yyyy년 MM월", { locale: ko }),
                        }}
                        className={`[&_.rdp-day]:px-0 [&_.rdp-day]:w-7 ${
                            /* 날짜 위에 점을 찍을 여백이 필요해 점을 쓰는 달력만 칸을 키운다 */
                            markedDates && markedDates.length > 0 ? "[&_.rdp-day]:h-10" : "[&_.rdp-day]:h-7"
                        }`}
                    />
                </div>
            )}
        </div>
    );
};

export default DayPickerInput;
